import React, { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from '@blocs.i18n';
import { captureExceptionEvent } from '@blocs.sentry';
import { Link, LinkAsButton } from '@minecraft.atoms';
import { isDateInFuture, subtractFromDate } from '@minecraft.date-utils';
import { gqlMedia, gqlMediaTypeCodeEnum } from '@minecraft.graphql-types';
import { MediaAssetDoc } from '@minecraft.graphql-operations';
import { useGlobalNotification } from '@minecraft.notification-provider';

type MediaLinkType = 'sides'; // list of types that can expand in the future, for now just one value for our sides use-case
type MediaLinkMediaObject = gqlMedia;

const MediaButtonLink = ({ mediaObject }: { mediaObject: MediaLinkMediaObject }) => {
  const client = useApolloClient();
  const { showMessage } = useGlobalNotification();
  const { t } = useTranslation();

  const refetchMediaAsset = useCallback(async () => {
    try {
      const res = await client.query({
        query: MediaAssetDoc,
        variables: { mediaAssetId: mediaObject?.mediaId || mediaObject?.id },
        fetchPolicy: 'network-only',
      });

      if (res?.errors?.length || !res?.data?.mediaAsset?.url) {
        throw res?.errors?.[0] || new Error('Unable to access media, please reload the page and try again.');
      }

      return { type: 'success', mediaAsset: res.data.mediaAsset };
    } catch (error) {
      showMessage({ message: t('common:toast.mediaAsset.failed'), color: 'danger' });
      captureExceptionEvent(error, { location: 'MediaLink.refetchMediaAsset', mediaId: mediaObject?.id });
      return { type: 'error', error };
    }
  }, [client, mediaObject?.mediaId, mediaObject?.id, showMessage, t]);

  const handleClick = useCallback(async () => {
    if (!mediaObject) {
      return;
    }

    let urlToOpen = mediaObject.url;

    // if the media will expire in less than 5 minutes,
    // we need to refetch the media asset to get a new URL that will be valid
    const paddedFutureDate = subtractFromDate({ date: mediaObject.urlExpiresAt, amount: 5, unit: 'minutes' });
    if (!urlToOpen || !isDateInFuture({ isoDateString: paddedFutureDate, includeSame: true })) {
      const { type, mediaAsset } = await refetchMediaAsset();

      // error handling is done by refetchMediaAsset function, we just return early if there was an error
      // because we don't have a valid URL to open
      if (type !== 'success') {
        return;
      }
      urlToOpen = mediaAsset.url;
    }

    // with a valid URL in hand, we can open it in a new tab for the user
    window.open(urlToOpen, '_blank', 'noopener,noreferrer');
  }, [mediaObject, refetchMediaAsset]);

  return <LinkAsButton onClick={handleClick}>{mediaObject.name}</LinkAsButton>;
};

export type MediaLinkProps = {
  mediaObject: MediaLinkMediaObject;
  type: MediaLinkType;
};

export const MediaLink = ({ mediaObject, type }: MediaLinkProps) => {
  const canExpire = mediaObject?.authenticated && mediaObject?.urlExpiresAt;

  if (!mediaObject || !['sides'].includes(type) || mediaObject?.mediaType?.code !== gqlMediaTypeCodeEnum.DOCUMENT) {
    return null;
  }

  return !canExpire ? (
    <Link to={mediaObject.url} target="_blank">
      {mediaObject.name}
    </Link>
  ) : (
    <MediaButtonLink mediaObject={mediaObject} />
  );
};
